/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
import { NgClass, NgIf } from '@angular/common';
import { Component, Injector } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FieldWrapper } from '@ngx-formly/core';
import { FeedbackService, LoadingService, ReportService } from '@app/core/services';
import { ReportDocumentFormat } from '@app/enums/report-type';
import { ResponseCodes } from '@app/enums/response-codes.enum';
import { FormComponent } from './form.component';

@Component({
	selector: 'formly-basic-wrapper',
	template: `
		<div class="{{ props.class }}">
			<ng-container #fieldComponent></ng-container>
		</div>
	`,
})
export class BasicWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'formly-custom-wrapper',
	template: `
		<div class="formly-custom-panel container-fluid">
			<ng-container #fieldComponent></ng-container>
		</div>
	`,
})
export class CustomWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'formly-section-simple-wrapper',
	template: ` <div class="simple-section"><ng-container #fieldComponent></ng-container></div> `,
	standalone: true,
})
export class SectionSimpleWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'formly-section-simple-title-wrapper',
	template: `
		<div
			class="simple-section"
			[ngClass]="{ 'remove-lr-padding': props.noLeftRightPadding, 'remove-tb-padding': props.noTopBottomPadding }"
			[hidden]="props.hidden"
		>
			<h3 [hidden]="props.useSubHeading === true" class="linq-heading">{{ props.label }}</h3>
			<h4 [hidden]="props.useSubHeading !== true" class="linq-heading">{{ props.label }}</h4>
			<ng-container #fieldComponent></ng-container>
		</div>
	`,
	standalone: true,
	imports: [NgClass],
})
export class SectionSimpleTitleWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'formly-custom-panel-with-title-wrapper',
	template: `
		<div>
			<h3 [hidden]="props.useSubHeading === true || props.hideHeading === true" class="linq-heading">{{ props.label }}</h3>
			<h4 [hidden]="props.useSubHeading !== true || props.hideHeading === true" class="linq-heading">{{ props.label }}</h4>
			<div class="formly-custom-panel  container-fluid">
				<ng-container #fieldComponent></ng-container>
			</div>
		</div>
	`,
})
export class CustomPanelWithTitleWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'formly-section-single-field-accordion-wrapper',
	template: `
		<mat-expansion-panel hideToggle expanded class="{{ props.class || 'expansion-panel-padding-default' }}">
			<mat-expansion-panel-header *ngIf="props.excludePanelHeader !== true" [style.pointerEvents]="'none'">
				{{ props.label }}
			</mat-expansion-panel-header>
			<ng-container #fieldComponent></ng-container>
		</mat-expansion-panel>
	`,
	standalone: true,
	imports: [MatExpansionModule, NgIf],
})
export class SectionSingleFieldWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'formly-section-accordion-wrapper',
	template: `
		<mat-expansion-panel [expanded]="isExpanded(props.section)">
			<mat-expansion-panel-header>
				<h3 class="linq-heading" [style.marginBottom]="0">{{ props.label }}</h3>
			</mat-expansion-panel-header>
			<ng-container #fieldComponent></ng-container>
		</mat-expansion-panel>
	`,
	standalone: true,
	imports: [MatExpansionModule],
})
export class SectionAccordionWrapperComponent extends FieldWrapper {
	isExpanded(section: string): boolean {
		return Array.isArray(this.props.sectionsOpened) && this.props.sectionsOpened.includes(section);
	}
}

@Component({
	selector: 'formly-section-wrapper',
	template: `
		<mat-card-title class="linq-card__header">
			<h3 class="linq-heading">{{ props.label }}</h3>
			<span class="spacer"></span>
			<button *ngIf="!isReadOnly" type="button" mat-icon-button aria-label="Edit" (click)="edit(props.section)">
				<mat-icon>mode</mat-icon>
			</button>
		</mat-card-title>
		<mat-card-content class="linq-card__content">
			<div class="linq-key-value-list">
				<ng-container #fieldComponent></ng-container>
			</div>
		</mat-card-content>
	`,
	standalone: true,
	imports: [MatCardModule, NgIf, MatButtonModule, MatIconModule],
})
export class SectionWrapperComponent extends FieldWrapper {
	_formComponent: FormComponent;

	// retrieve parent FormComponent
	get formComponent(): FormComponent {
		if (!this._formComponent) {
			this._formComponent = this.injector.get(FormComponent);
		}
		return this._formComponent;
	}

	constructor(private readonly injector: Injector) {
		super();
	}

	public get isReadOnly(): boolean {
		return this.formComponent?.isReadOnly;
	}

	public edit(section: string): void {
		this.formComponent?.edit(section);
	}
}

@Component({
	selector: 'formly-report-wrapper',
	template: `
		<ng-container #fieldComponent></ng-container>
		<div class="col-xs-12 button-container">
			<button
				*ngIf="field.templateOptions.hasGenerateReport !== undefined ? field.templateOptions.hasGenerateReport : true"
				class="linq-button--primary export-button mx-1"
				type="submit"
				color="primary"
				mat-flat-button
				(click)="generateReport()"
				[disabled]="formControl.invalid"
			>
				Generate Report
			</button>
			<button
				*ngIf="field.templateOptions.hasExcelExport !== undefined ? field.templateOptions.hasExcelExport : true"
				class="linq-button--primary export-button mx-1"
				type="button"
				color="primary"
				mat-flat-button
				(click)="generateExcel()"
				[disabled]="formControl.invalid"
			>
				Export File
			</button>
		</div>
	`,
	styleUrls: ['./form-group-wrapper.component.less'],
	standalone: true,
	imports: [NgIf, MatButtonModule],
})
export class SectionReportWrapperComponent extends FieldWrapper {
	constructor(
		private readonly reportService: ReportService,
		private readonly loadingService: LoadingService,
		private readonly feedbackService: FeedbackService
	) {
		super();
	}

	generateReport(): void {
		let newModel;
		if (this.field.props.onGenerateReport) {
			this.field.props.onGenerateReport();
		}
		if (this.field.props.customModel) {
			newModel = { ...this.field.props.customModel(ReportDocumentFormat.Pdf) };
		}
		let url = this.field.props.reportURL;
		if (!this.field.props.URLwithoutReport) {
			url = this.field.props.reportURL + 'report';
		}
		newModel = newModel || this.model;

		if (this.field.props.UseHangfire) {
			this.postHfReport(url, newModel);
		} else {
			this.loadingService.show();
			const fileName = this.field.props.fileName + 'Report.pdf';
			if (this.field.props.reportType === 'get' || this.field.props.reportType === undefined) {
				this.getReport(url, fileName, newModel);
			} else {
				this.postReport(url, fileName, newModel);
			}
		}
	}

	getReport(url: string, fileName: string, model): void {
		this.reportService.getReport({}, url, model, fileName, (status: number) => {
			this.reportFeedback(status);
			this.loadingService.hide();
		});
	}

	postReport(url: string, fileName: string, model): void {
		if (this.field.props.callback) {
			this.reportService.postQueueReport(url, model, this.field.props.callback);
			this.loadingService.hide();
		} else {
			this.reportService.postReport({}, url, model, fileName, (status: number) => {
				this.reportFeedback(status);
				this.loadingService.hide();
			});
		}
	}

	postHfReport(url: string, model): void {
		this.reportService.postHfReport(url, model).subscribe((status) => {
			if (status) {
				if (this.field.props.callback) {
					this.field.props.callback(status);
				} else {
					this.reportService.responseCodeHandler(status, this.field.props.reportName);
				}
			}
		});
	}

	generateExcel(): void {
		let newModel;
		if (this.field.props.onExportReport) {
			this.field.props.onExportReport();
		}
		if (this.field.props.customModel) {
			newModel = { ...this.field.props.customModel(ReportDocumentFormat.Excel) };
		}
		let url = this.field.props.reportURL;
		if (this.field.props.excelURL) {
			url = this.field.props.excelURL;
		}
		if (!this.field.props.URLwithoutExcel) {
			url = url + 'excel';
		}
		newModel = newModel || this.model;
		if (this.field.props.UseHangfire) {
			this.postHfReport(url, newModel);
		} else {
			this.loadingService.show();
			const fileName = this.field.props.fileName + '.xlsx';
			if (this.field.props.reportType === 'get' || this.field.props.reportType === undefined) {
				this.getExcel(url, fileName, newModel);
			} else {
				this.postExcel(url, fileName, newModel);
			}
		}
	}

	getExcel(url: string, fileName: string, model): void {
		this.reportService.getExcel({}, url, model, fileName, (status: number): void => {
			this.loadingService.hide();
			this.reportFeedback(status);
		});
	}

	postExcel(url: string, fileName: string, model): void {
		this.reportService.postExcel({}, url, model, fileName, (status: number): void => {
			this.reportFeedback(status);
			this.loadingService.hide();
		});
	}

	reportFeedback(status: number): void {
		if (status === ResponseCodes.Ok || status === ResponseCodes.Accepted) {
			this.feedbackService.customSuccess((this.field.props.reportName ?? '') + ' Report was queued successfully');
		}
	}
}

@Component({
	selector: 'formly-report-field-wrapper',
	template: `
		<mat-card class="linq-card">
			<h3 class="report-header">{{ props.label }}</h3>
			<mat-card-content>
				<ng-container #fieldComponent></ng-container>
				<div *ngIf="props.customError">
					<mat-error> {{ props.errorMessage }} </mat-error>
				</div>
			</mat-card-content>
		</mat-card>
	`,
	styleUrls: ['./form-group-wrapper.component.less'],
	standalone: true,
	imports: [MatCardModule, NgIf, MatFormFieldModule],
})
export class SectionReportFieldWrapperComponent extends FieldWrapper {}

@Component({
	selector: 'panel-default-wrapper',
	template: `
		<div class="panel panel-default">
			<div class="panel-body">
				<span class="bold">{{ props.label }}</span>
			</div>
			<div class="panel-footer container-fluid">
				<ng-container #fieldComponent></ng-container>
			</div>
		</div>
	`,
})
export class PanelDefaultWrapperComponent extends FieldWrapper {}
